import React, { Suspense, useContext, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useMatches, useSearchParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box, ThemeProvider } from '@factorialco/deprecated-design-system'
import { EnvironmentContext } from 'config/environment'

import { assignAmplitudeGroup } from 'modules/core/components/Bootstrap/afterFetchData/assignAmplitudeGroup'
import runAmplitude from 'modules/core/components/Bootstrap/afterFetchData/runAmplitude'
import runDatadog from 'modules/core/components/Bootstrap/afterFetchData/runDatadog'
import { runDevtools } from 'modules/core/components/Bootstrap/afterFetchData/runDevtools'
import runFacebookPixel from 'modules/core/components/Bootstrap/afterFetchData/runFacebookPixel'
import runI18nSetup from 'modules/core/components/Bootstrap/afterFetchData/runI18nSetup'
import runPerformanceLog from 'modules/core/components/Bootstrap/afterFetchData/runPerformanceLog'
import runSentry from 'modules/core/components/Bootstrap/afterFetchData/runSentry'
import {
  CollectionBootstrapConfig,
  CollectionsBootstrap,
} from 'modules/core/components/CollectionsBootstrap'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getFactorialData } from 'modules/core/lib/cookie'
import { setFrontendModule } from 'modules/core/lib/datadogLogger'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import hasFeature from 'modules/core/lib/hasFeature'
import { apiClient } from 'modules/core/lib/mobx-rest'
import { signOutAndRedirect } from 'modules/core/lib/signOutAndRedirect'
import companies from 'modules/core/store/collections/companies'
import features from 'modules/core/store/collections/features'

import { hasOnboardingEnabled } from 'modules/employee_onboarding/lib/utils'
import { isManager } from 'modules/employees/lib/isManager'
import { canAccessWelcomeSpace, hasRegularAccessOrNotEmployee } from 'modules/employees/lib/utils'
import PageLoading from 'modules/layout/components/Page/Loading'
import RootRedirects from 'modules/layout/components/RootRedirects'

interface RouterState {
  location: {
    pathname: string
  }
  routes?: string[]
}

const useTrackPage = () => {
  const { pathname } = useLocation()
  const prevStateRef = React.useRef<RouterState | null>(null)

  useEffect(() => {
    const nextState: RouterState = {
      location: {
        pathname,
      },
    }

    const prevState = prevStateRef.current

    // We should think in a more resilient way to access this information
    sessionStorage.setItem(
      'rootRouteTransition',
      JSON.stringify({
        prevState,
        nextState,
      })
    )

    prevStateRef.current = nextState

    document.documentElement.scrollTo(0, 0)
  }, [pathname])
}

const useModuleMatches = () => {
  const matches = useMatches()

  const moduleMatch = matches.find(
    (match) => typeof match.handle === 'object' && 'module' in (match.handle ?? {})
  )

  const module =
    moduleMatch &&
    moduleMatch.handle &&
    typeof moduleMatch.handle === 'object' &&
    'module' in moduleMatch.handle
      ? moduleMatch.handle.module + ''
      : undefined

  setFrontendModule(module)
}

const Container = () => {
  const environment = useContext(EnvironmentContext)
  const [searchParams] = useSearchParams()
  const { amplitudeEnabled, amplitudeDevLogsEnabled, facebookPixelEnabled, devtoolsEnabled } =
    environment
  const location = useLocation()
  const { navigation } = useNavigation()
  const client = useApolloClient()

  const {
    access,
    email,
    company,
    employee,
    canRequestUpsell,
    canSeeProductUpdates,
    canSelfUpsell,
  } = useCurrentSession()

  // NOTE: coupled to collections. Consider using current session from GraphQL if possible
  const selectedCompany = companies.selected

  const [state, setState] = useState<{ loading: boolean; error: unknown }>({
    loading: true,
    error: null,
  })

  useTrackPage()
  useModuleMatches()

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await runI18nSetup(access.locale, firstDayOfWeekAsIndex(access.firstDayOfWeek))

        runSentry({
          locale: access.locale,
          companyId: company.id,
          companyName: company.name,
          companySize: company.size,
          userId: access.id,
        })

        const { userId } = getFactorialData() || {}

        if (userId) {
          runDatadog({
            userId,
            accessId: access.id,
            companyId: company.id,
            companyName: company.name,
            datadogEnvironment: environment.datadogEnvironment,
            allowedCompanyIds: environment.datadogRumAllowedCompanyIds,
          })
        }

        const switchToCompanyId = searchParams.get('switchToCompanyId')
        const redirect_uri = searchParams.get('redirect_uri')

        if (switchToCompanyId && switchToCompanyId !== selectedCompany?.id) {
          const targetCompany = companies.find({
            id: switchToCompanyId,
          })

          if (targetCompany) {
            await targetCompany.rpc('select_current')
            window.location.href = location.pathname

            return
          } else {
            client.clearStore()
            signOutAndRedirect({
              preloadDemoLogin: environment.preloadDemoLogin,
              redirect_uri,
            })

            return
          }
        }

        runPerformanceLog({
          appIdentifier: 'time_to_interactive',
          companyId: company.id,
          companyName: company.name,
          companySize: company.size,
          userId: access.id,
          userEmail: email,
          userIsAdmin: false,
        })

        if (amplitudeEnabled) {
          runAmplitude({
            environment,
            doNotTrack: hasFeature('do_not_track'),
            analyticsConsent: access.analyticsConsent ?? true,
            userId: access.id,
            userFirstName: access.firstName,
            userLastName: access.lastName,
            userEmail: email,
            userCanRequestUpsell: canRequestUpsell,
            userCanSeeProductUpdates: canSeeProductUpdates,
            userIsManager: isManager(employee),
            userCanAccessWelcomeSpace: canAccessWelcomeSpace(employee),
            companyId: company.id,
            companyName: company.name,
            companySize: company.size,
            companyIndustry: company.industry ?? '',
            companyCountry: company.legalEntities.find(({ main }) => main)?.country ?? '',
            subscriptionPlan: company.subscription.plan,
            subscriptionAtsPlan: company.subscription.atsPlan ?? '',
            subscriptionCurrentCycleQuantity: company.subscription?.currentCycleQuantity ?? null,
            canSelfUpsell: canSelfUpsell,
          })

          const companyIds = companies.models.flatMap((c) => `${c.id}`)
          assignAmplitudeGroup('company', companyIds)
        } else {
          // If amplitude is not enabled we need to check if we want to enable the debug logs if
          // ENV var AMPLITUDE_DEV_LOGS_ENABLED is true is set
          window.logEvents = amplitudeDevLogsEnabled
        }

        facebookPixelEnabled &&
          runFacebookPixel({
            environment,
            doNotTrack: hasFeature('do_not_track'),
            analyticsConsent: access.analyticsConsent,
          })
        apiClient().post('/visits')

        devtoolsEnabled && runDevtools()

        setState({ loading: false, error: null })
      } catch (error) {
        setState({ loading: false, error })
      }
    }

    bootstrap()
  }, [])

  // if we don't know which company to use, ask first and follow the deep link next
  const needToSelectCompany =
    !selectedCompany && location.pathname !== navigation.authentication.company_selector.fullPath()

  if (needToSelectCompany) {
    return (
      <Navigate
        to={navigation.authentication.company_selector.fullPath()}
        state={{ returnTo: location.pathname }}
      />
    )
  }

  if (location.pathname === '/') {
    return (
      <RootRedirects
        hasOnboardingEnabled={hasOnboardingEnabled()}
        hasRegularAccessOrNotEmployee={hasRegularAccessOrNotEmployee(employee)}
      />
    )
  }

  if (state.loading) {
    return (
      <Box width="full" height="full" justifyContent="center" alignItems="center">
        <PageLoading />
      </Box>
    )
  }

  if (state.error) {
    throw state.error
  }

  return (
    <Suspense>
      <Outlet />
    </Suspense>
  )
}

const ApplicationRootPage = () => {
  const collections: CollectionBootstrapConfig[] = [
    [companies, {}],
    [features, {}],
  ]

  return (
    <CollectionsBootstrap collections={collections} loadingFallback={null}>
      <ThemeProvider theme="legacy">
        <Container />
      </ThemeProvider>
    </CollectionsBootstrap>
  )
}

export default ApplicationRootPage
